import React, { useState, useEffect, useCallback } from 'react';
import { TextField, Alert, Typography, Box, Paper } from '@mui/material';
import DetailsEditor from './DetailsEditor';
import { useApiClient } from '../hooks/useApiClient';
import './UserEdit.scss';

interface User {
  ownerId: string;
  email: string;
  firstName: string;
  lastName: string;
  status: string;
  tier: string;
  createdAt: string;
  updatedAt?: string;
}

interface DetailsEditorActions {
  onSave?: () => Promise<void> | void;
  onDelete?: () => Promise<void> | void;
  onCancel?: () => void;
}

interface DetailsEditorState {
  saveEnabled?: boolean;
  deleteEnabled?: boolean;
  saving?: boolean;
  deleting?: boolean;
}

interface UserEditFormProps {
  editorActions?: DetailsEditorActions;
  setEditorState?: (state: Partial<DetailsEditorState>) => void;
}

const UserEditForm: React.FC<UserEditFormProps> = ({ editorActions = {}, setEditorState = () => {} }) => {
  const [user, setUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({ firstName: '', lastName: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const apiClient = useApiClient();

  const isFormValid = formData.firstName.trim() !== '' && formData.lastName.trim() !== '';

  const fetchUser = useCallback(async () => {
    try {
      const response = await apiClient.get('/user');

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setFormData({ firstName: userData.firstName || '', lastName: userData.lastName || '' });
      } else {
        setError('Failed to load user data');
      }
    } catch (err) {
      setError('Error loading user data');
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const handleSave = useCallback(async () => {
    if (!isFormValid) {
      setError('First name and last name cannot be empty');
      return;
    }

    setError(null);
    setSuccess(null);

    try {
      const response = await apiClient.put('/user', {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim()
      });

      if (response.ok) {
        const result = await response.json();
        setUser(result.user);
        setSuccess('Profile updated successfully!');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update user');
      }
    } catch (err) {
      setError('Error updating user');
    }
  }, [formData, isFormValid, apiClient]);

  useEffect(() => {
    setEditorState({ saveEnabled: isFormValid });
  }, [isFormValid, setEditorState]);

  useEffect(() => {
    editorActions.onSave = async () => {
      setEditorState({ saving: true });
      try {
        await handleSave();
      } finally {
        setEditorState({ saving: false });
      }
    };
  }, [editorActions, handleSave, setEditorState]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isFormValid && editorActions.onSave) {
      e.preventDefault();
      editorActions.onSave();
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <div>Unable to load user data</div>;
  }

  return (
    <div>
      {error && (
        <Alert severity="error" className="user-edit__alert">
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" className="user-edit__alert">
          {success}
        </Alert>
      )}

      <Paper className="user-edit__paper">
        <Typography variant="h6" gutterBottom>
          Account Information
        </Typography>
        <Box className="user-edit__account-info">
          <Typography variant="body2"><strong>User ID:</strong> {user.ownerId}</Typography>
          <Typography variant="body2"><strong>Email:</strong> {user.email}</Typography>
          <Typography variant="body2"><strong>Status:</strong> {user.status}</Typography>
          <Typography variant="body2"><strong>Tier:</strong> {user.tier}</Typography>
          <Typography variant="body2"><strong>Member since:</strong> {new Date(user.createdAt).toLocaleDateString()}</Typography>
        </Box>
      </Paper>

      <Paper className="user-edit__paper">
        <Typography variant="h6" gutterBottom>
          Profile Details
        </Typography>
        <Box className="user-edit__form-fields" onKeyDown={handleKeyDown} tabIndex={0}>
          <TextField
            label="First Name"
            value={formData.firstName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, firstName: e.target.value })}
            required
            fullWidth
            variant="outlined"
            error={!formData.firstName.trim()}
            helperText={!formData.firstName.trim() ? 'First name is required' : ''}
          />
          <TextField
            label="Last Name"
            value={formData.lastName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, lastName: e.target.value })}
            required
            fullWidth
            variant="outlined"
            error={!formData.lastName.trim()}
            helperText={!formData.lastName.trim() ? 'Last name is required' : ''}
          />
        </Box>
      </Paper>
    </div>
  );
};

const UserEdit: React.FC = () => {
  return (
    <div className="user-edit">
      <DetailsEditor
        title="Edit Profile"
        showSave={true}
        showDelete={false}
        showCancel={false}
        onComplete={() => {}}
      >
        <UserEditForm />
      </DetailsEditor>
    </div>
  );
};

export default UserEdit;